import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Loader2, Paperclip, Phone, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, API, setAuthToken, formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700",
  Contacted: "bg-amber-100 text-amber-700",
  Quoted: "bg-purple-100 text-purple-700",
  Booked: "bg-emerald-100 text-emerald-700",
  Completed: "bg-slate-200 text-slate-700",
  Lost: "bg-red-100 text-red-700",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("leads");
  const [contracts, setContracts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [landlords, setLandlords] = useState([]);

  const loadLandlords = useCallback(async () => {
    try {
      const { data } = await api.get("/landlord-enquiries");
      setLandlords(data.enquiries);
    } catch (err) {
      if (err.response?.status === 401) { setAuthToken(null); navigate("/admin/login"); }
    }
  }, [navigate]);

  const changeLandlordStatus = async (id, status) => {
    try {
      await api.patch(`/landlord-enquiries/${id}/status`, { status });
      setLandlords((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Could not update status"); }
  };

  const loadContracts = useCallback(async () => {
    try {
      const { data } = await api.get("/contracts");
      setContracts(data.contracts);
    } catch (err) {
      if (err.response?.status === 401) { setAuthToken(null); navigate("/admin/login"); }
    }
  }, [navigate]);

  const loadPackages = useCallback(async () => {
    try {
      const { data } = await api.get("/packages");
      setPackages(data.packages);
    } catch (err) {
      if (err.response?.status === 401) { setAuthToken(null); navigate("/admin/login"); }
    }
  }, [navigate]);

  const changePackageStatus = async (id, status) => {
    try {
      await api.patch(`/packages/${id}/status`, { status });
      setPackages((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Could not update status"); }
  };

  const changeContractStatus = async (id, status) => {
    try {
      await api.patch(`/contracts/${id}/status`, { status });
      setContracts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Could not update status"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", { params: filter !== "all" ? { status: filter } : {} });
      setLeads(data.leads);
      setCounts(data.counts);
      setStatuses(data.statuses);
    } catch (err) {
      if (err.response?.status === 401) { setAuthToken(null); navigate("/admin/login"); }
      else toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    if (!localStorage.getItem("moveout_token")) { navigate("/admin/login"); return; }
    load();
    loadContracts();
    loadPackages();
    loadLandlords();
  }, [load, loadContracts, loadPackages, loadLandlords, navigate]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Marked as ${status}`);
      const { data } = await api.get("/leads", { params: filter !== "all" ? { status: filter } : {} });
      setCounts(data.counts);
      if (filter !== "all") setLeads(data.leads);
    } catch (err) {
      toast.error("Could not update status");
    }
  };

  const logout = () => { setAuthToken(null); navigate("/admin/login"); };

  const viewFile = async (path) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not open file"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <h1 className="font-display text-xl font-black text-slate-900">Leads Dashboard</h1>
          <div className="flex items-center gap-2">
            <div className="mr-2 flex rounded-full bg-slate-100 p-1">
              <button onClick={() => setView("leads")} data-testid="tab-leads" className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${view === "leads" ? "bg-white text-emerald-700 shadow" : "text-slate-500"}`}>Leads</button>
              <button onClick={() => setView("contracts")} data-testid="tab-contracts" className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${view === "contracts" ? "bg-white text-emerald-700 shadow" : "text-slate-500"}`}>Contracts{contracts.length ? ` (${contracts.length})` : ""}</button>
              <button onClick={() => setView("packages")} data-testid="tab-packages" className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${view === "packages" ? "bg-white text-emerald-700 shadow" : "text-slate-500"}`}>Packages{packages.length ? ` (${packages.length})` : ""}</button>
              <button onClick={() => setView("landlords")} data-testid="tab-landlords" className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${view === "landlords" ? "bg-white text-emerald-700 shadow" : "text-slate-500"}`}>Landlords{landlords.length ? ` (${landlords.length})` : ""}</button>
            </div>
            <button onClick={() => { load(); loadContracts(); loadPackages(); loadLandlords(); }} data-testid="refresh-btn" className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 hover:border-emerald-500">
              <RefreshCw size={15} /> Refresh
            </button>
            <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {view === "leads" && (
        <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
          <button onClick={() => setFilter("all")} data-testid="filter-all"
            className={`rounded-2xl border p-4 text-left transition-all ${filter === "all" ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white"}`}>
            <p className="text-2xl font-black text-slate-900">{Object.values(counts).reduce((a, b) => a + b, 0)}</p>
            <p className="text-xs font-bold uppercase text-slate-500">All</p>
          </button>
          {statuses.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.toLowerCase()}`}
              className={`rounded-2xl border p-4 text-left transition-all ${filter === s ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white"}`}>
              <p className="text-2xl font-black text-slate-900">{counts[s] || 0}</p>
              <p className="text-xs font-bold uppercase text-slate-500">{s}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div>
          ) : leads.length === 0 ? (
            <div className="py-20 text-center text-slate-400" data-testid="no-leads">No leads yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {leads.map((l) => (
                <div key={l.id} className="p-5 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display text-lg font-bold text-slate-900">{l.full_name}</span>
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[l.status] || "bg-slate-100"}`}>{l.status}</span>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        {l.property_type} • {l.property_location} • {l.bedrooms || "?"} bed / {l.bathrooms || "?"} bath
                      </p>
                      <div className="mt-2 flex flex-wrap gap-4 text-sm">
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-emerald-700"><Phone size={13} /> {l.phone}</a>
                        <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-emerald-700"><Mail size={13} /> {l.email}</a>
                        {l.preferred_date && <span className="text-slate-500">📅 {l.preferred_date} {l.preferred_time}</span>}
                      </div>
                      {l.services?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {l.services.map((s) => <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{s}</span>)}
                        </div>
                      )}
                      <button onClick={() => setSelected(selected === l.id ? null : l.id)} className="mt-2 text-xs font-bold text-emerald-700 hover:underline">
                        {selected === l.id ? "Hide details" : "View details"}
                      </button>
                      {selected === l.id && (
                        <div className="mt-3 space-y-2 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
                          <p><strong>Condition:</strong> {l.property_condition || "-"}</p>
                          <p><strong>Empty:</strong> {l.is_empty || "-"} • <strong>Parking:</strong> {l.parking || "-"}</p>
                          <p><strong>Message:</strong> {l.message || "-"}</p>
                          <p><strong>Source:</strong> {l.source_page} • <strong>Received:</strong> {new Date(l.created_at).toLocaleString("en-IE")}</p>
                          {l.uploaded_files?.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-1">
                              {l.uploaded_files.map((f) => (
                                <button key={f.id} onClick={() => viewFile(f.path)} className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-100">
                                  <Paperclip size={12} /> {f.original_filename} <ExternalLink size={11} />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="w-40">
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger data-testid={`status-select-${l.id}`} className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}

        {view === "contracts" && (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="contracts-list">
            {contracts.length === 0 ? (
              <div className="py-20 text-center text-slate-400" data-testid="no-contracts">No contract enquiries yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {contracts.map((c) => (
                  <div key={c.id} className="p-5 hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-display text-lg font-bold text-slate-900">{c.business_name}</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[c.status] || "bg-slate-100"}`}>{c.status}</span>
                        </div>
                        <p className="mt-1 text-sm text-slate-500">{c.contact_person} • {c.premises_type || "—"} • {c.frequency || "—"}</p>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm">
                          <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-emerald-700"><Phone size={13} /> {c.phone}</a>
                          <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-emerald-700"><Mail size={13} /> {c.email}</a>
                        </div>
                        {c.services?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">{c.services.map((s) => <span key={s} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{s}</span>)}</div>
                        )}
                        <p className="mt-2 text-xs text-slate-400">{c.business_address} • Start: {c.start_date || "—"} • {new Date(c.created_at).toLocaleString("en-IE")}</p>
                        {c.message && <p className="mt-1 text-sm text-slate-600">{c.message}</p>}
                      </div>
                      <div className="w-40">
                        <Select value={c.status} onValueChange={(v) => changeContractStatus(c.id, v)}>
                          <SelectTrigger data-testid={`contract-status-${c.id}`} className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "packages" && (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="packages-admin-list">
            {packages.length === 0 ? (
              <div className="py-20 text-center text-slate-400" data-testid="no-packages">No package enquiries yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {packages.map((p) => (
                  <div key={p.id} className="p-5 hover:bg-slate-50" data-testid={`package-row-${p.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-display text-lg font-bold text-slate-900">{p.full_name}</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[p.status] || "bg-slate-100"}`}>{p.status}</span>
                          {p.package && <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">{p.package}</span>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm">
                          <a href={`tel:${p.phone}`} className="flex items-center gap-1 text-emerald-700"><Phone size={13} /> {p.phone}</a>
                          <a href={`mailto:${p.email}`} className="flex items-center gap-1 text-emerald-700"><Mail size={13} /> {p.email}</a>
                        </div>
                        <p className="mt-2 text-sm text-slate-500">From: {p.collection_address || "—"} → To: {p.delivery_address || "—"}</p>
                        <p className="mt-1 text-sm text-slate-500">{p.property_type || "—"} • {p.bedrooms || "?"} bed • Move: {p.moving_date || "—"} • Clean: {p.cleaning_date || "—"}</p>
                        <p className="mt-1 text-sm text-slate-500">Cleaning: {p.cleaning_required || "—"} • Rubbish: {p.rubbish_removal_required || "—"} • Assembly: {p.furniture_assembly_required || "—"}</p>
                        {p.items_to_move && <p className="mt-1 text-sm text-slate-600"><strong>Items:</strong> {p.items_to_move}</p>}
                        {p.heavy_items && <p className="mt-1 text-sm text-slate-600"><strong>Heavy:</strong> {p.heavy_items}</p>}
                        {p.message && <p className="mt-1 text-sm text-slate-600">{p.message}</p>}
                        {p.uploaded_files?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {p.uploaded_files.map((f) => (
                              <button key={f.id} onClick={() => viewFile(f.path)} className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-100">
                                <Paperclip size={12} /> {f.original_filename} <ExternalLink size={11} />
                              </button>
                            ))}
                          </div>
                        )}
                        <p className="mt-2 text-xs text-slate-400">{new Date(p.created_at).toLocaleString("en-IE")}</p>
                      </div>
                      <div className="w-40">
                        <Select value={p.status} onValueChange={(v) => changePackageStatus(p.id, v)}>
                          <SelectTrigger data-testid={`package-status-${p.id}`} className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === "landlords" && (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="landlords-admin-list">
            {landlords.length === 0 ? (
              <div className="py-20 text-center text-slate-400" data-testid="no-landlords">No landlord / agent enquiries yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {landlords.map((l) => (
                  <div key={l.id} className="p-5 hover:bg-slate-50" data-testid={`landlord-row-${l.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-display text-lg font-bold text-slate-900">{l.company_name || l.contact_person}</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[l.status] || "bg-slate-100"}`}>{l.status}</span>
                          {l.work_type && <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">{l.work_type}</span>}
                        </div>
                        <p className="mt-1 text-sm text-slate-500">{l.contact_person} • {l.service_required || "—"} • {l.property_type || "—"} {l.bedrooms ? `• ${l.bedrooms} bed` : ""}</p>
                        <div className="mt-2 flex flex-wrap gap-4 text-sm">
                          <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-emerald-700"><Phone size={13} /> {l.phone}</a>
                          <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-emerald-700"><Mail size={13} /> {l.email}</a>
                        </div>
                        <p className="mt-1 text-sm text-slate-500">{l.property_address || "—"} • Properties managed: {l.num_properties || "—"} • Preferred: {l.preferred_date || "—"}</p>
                        {l.message && <p className="mt-1 text-sm text-slate-600">{l.message}</p>}
                        <p className="mt-2 text-xs text-slate-400">{new Date(l.created_at).toLocaleString("en-IE")}</p>
                      </div>
                      <div className="w-40">
                        <Select value={l.status} onValueChange={(v) => changeLandlordStatus(l.id, v)}>
                          <SelectTrigger data-testid={`landlord-status-${l.id}`} className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
